import { useState, useCallback } from 'react';
import type { Driver } from '../types';
import {
    fetchDriversFromDb,
    fetchAvailableDriversFromDb,
    fetchDriverDeliveryStatusesFromDb,
    updateDriverInDb,
    updateDriverPhoneInDb,
    fetchDriverPhoneFromDb,
    fetchDriverPhonesFromDb,
    deleteDriverFromDb,
    type DriverDeliveryStatus,
    type UpdateDriverPayload,
} from '../services/driverService';
import { getErrorMessage } from '../utils/errors';

export function useDrivers() {
    const [drivers,          setDrivers]          = useState<Driver[]>([]);
    const [availableDrivers, setAvailableDrivers] = useState<Driver[]>([]);
    const [deliveryStatuses, setDeliveryStatuses] = useState<DriverDeliveryStatus[]>([]);
    const [loading,          setLoading]          = useState(false);
    const [error,            setError]            = useState<string | null>(null);

    const fetchDrivers = useCallback(async (): Promise<Driver[]> => {
        try {
            const data = await fetchDriversFromDb();
            setDrivers(data);
            return data;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch drivers'));
            return [];
        }
    }, []);

    const fetchAvailableDrivers = useCallback(async (): Promise<Driver[]> => {
        try {
            const data = await fetchAvailableDriversFromDb();
            setAvailableDrivers(data);
            return data;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch available drivers'));
            return [];
        }
    }, []);

    const fetchDriverDeliveryStatuses = useCallback(async (): Promise<DriverDeliveryStatus[]> => {
        try {
            const data = await fetchDriverDeliveryStatusesFromDb();
            setDeliveryStatuses(data);
            return data;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch delivery statuses'));
            return [];
        }
    }, []);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [drv, del] = await Promise.all([
                fetchDriversFromDb(),
                fetchDriverDeliveryStatusesFromDb(),
            ]);

            const authIds = drv.map(d => d.auth_id).filter((id): id is string => !!id);
            const phones = await fetchDriverPhonesFromDb(authIds).catch(() => ({} as Record<string, string>));

            setDrivers(drv.map(d => ({ ...d, cellphone: d.auth_id ? (phones[String(d.auth_id)] ?? '') : '' })));
            setDeliveryStatuses(del);
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load drivers'));
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchDriverPhone = useCallback(async (authId: string | number): Promise<string> => {
        try {
            return await fetchDriverPhoneFromDb(authId);
        } catch {
            return '';
        }
    }, []);

    const updateDriver = useCallback(async (
        driverId: string,
        patch: UpdateDriverPayload,
        phone?: string,
        authId?: string | number
    ): Promise<Driver | null> => {
        setError(null);
        try {
            const updated = await updateDriverInDb(driverId, patch);
            if (updated) {
                setDrivers(prev => prev.map(d => String(d.id) === driverId ? { ...d, ...updated } : d));
            }
            if (phone !== undefined && authId) {
                await updateDriverPhoneInDb(authId, phone);
            }
            return updated;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to update driver'));
            return null;
        }
    }, []);

    const deleteDriver = useCallback(async (authUuid: string): Promise<boolean> => {
        setError(null);
        try {
            const success = await deleteDriverFromDb(authUuid);
            if (success) setDrivers(prev => prev.filter(d => String(d.auth_id) !== authUuid));
            return success;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to delete driver'));
            return false;
        }
    }, []);

    return {
        drivers,
        availableDrivers,
        deliveryStatuses,
        loading,
        error,
        fetchAll,
        fetchDrivers,
        fetchAvailableDrivers,
        fetchDriverDeliveryStatuses,
        fetchDriverPhone,
        updateDriver,
        deleteDriver,
    };
}